/**
 * usePartyDefaults hook
 *
 * Fetches customer/supplier defaults from ERPNext when party is selected.
 * Returns currency, price list, conversion rate, taxes template, etc.
 *
 * This hook ensures ERPNext is the SINGLE SOURCE OF TRUTH for party defaults.
 * The returned values should be merged into the document form data.
 */

import { useState, useCallback } from "react";
import { useAuthStore } from "@/lib/store/authStore";
import { tenantServer } from "@/lib/api/tenantServerClient";

// Response type from the backend
export interface PartyDefaultsResult {
  // Company (resolved from user defaults or party)
  company?: string;
  // Currency and pricing
  currency?: string;
  selling_price_list?: string;
  buying_price_list?: string;
  price_list_currency?: string;
  plc_conversion_rate?: number;
  conversion_rate?: number;
  // Taxes
  taxes_and_charges?: string;
  tax_category?: string;
  // Addresses
  customer_address?: string;
  shipping_address_name?: string;
  address_display?: string;
  shipping_address?: string;
  // Contact
  contact_person?: string;
  contact_display?: string;
  contact_email?: string;
  contact_mobile?: string;
  // Payment terms
  payment_terms_template?: string;
  // Customer/Supplier info
  customer_name?: string;
  supplier_name?: string;
  customer_group?: string;
  territory?: string;
  // Sales team
  sales_team?: Array<Record<string, unknown>>;
}

export interface UsePartyDefaultsOptions {
  /** Party type: "Customer" or "Supplier" */
  partyType?: "Customer" | "Supplier";
  /** Doctype for the transaction (e.g., "Sales Order") */
  doctype?: string;
  /** Callback when defaults are fetched successfully */
  onSuccess?: (result: PartyDefaultsResult) => void;
  /** Callback when fetch fails */
  onError?: (error: string) => void;
}

export interface UsePartyDefaultsReturn {
  /**
   * Fetch party defaults for the given party name.
   * @param party - The party name (Customer/Supplier name)
   * @param company - Optional company override
   */
  fetchDefaults: (party: string, company?: string) => Promise<PartyDefaultsResult | null>;
  /** Latest defaults result */
  result: PartyDefaultsResult | null;
  /** Whether a fetch is in progress */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Clear the current result and error */
  reset: () => void;
}

/**
 * Hook to fetch party (Customer/Supplier) defaults from ERPNext
 *
 * @example
 * const { fetchDefaults, isLoading } = usePartyDefaults({
 *   partyType: "Customer",
 *   doctype: "Sales Order",
 *   onSuccess: (defaults) => {
 *     // Merge defaults into form data
 *     setFormData(prev => ({
 *       ...prev,
 *       currency: defaults.currency,
 *       selling_price_list: defaults.selling_price_list,
 *       plc_conversion_rate: defaults.plc_conversion_rate,
 *       taxes_and_charges: defaults.taxes_and_charges,
 *     }));
 *   },
 * });
 *
 * // When customer is selected
 * const handleCustomerChange = async (customerName) => {
 *   setFormData(prev => ({ ...prev, customer: customerName }));
 *   await fetchDefaults(customerName);
 * };
 */
export function usePartyDefaults(
  options: UsePartyDefaultsOptions = {}
): UsePartyDefaultsReturn {
  const {
    partyType = "Customer",
    doctype = "Sales Order",
    onSuccess,
    onError,
  } = options;

  const frappeClient = useAuthStore((state) => state.frappeClient);
  const authMode = useAuthStore((state) => state.authMode);
  const cpToken = useAuthStore((state) => state.cpToken);

  const [result, setResult] = useState<PartyDefaultsResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaults = useCallback(
    async (party: string, company?: string): Promise<PartyDefaultsResult | null> => {
      // Check authentication based on auth mode
      const isAuthenticated = authMode === "control_plane" ? !!cpToken : !!frappeClient;

      if (!isAuthenticated) {
        console.warn("[usePartyDefaults] Not authenticated - skipping fetch", { authMode });
        const errorMsg = "Not authenticated";
        setError(errorMsg);
        onError?.(errorMsg);
        return null;
      }

      if (!party) {
        console.warn("[usePartyDefaults] No party provided - skipping fetch");
        return null;
      }

      setIsLoading(true);
      setError(null);

      console.log("[usePartyDefaults] Fetching defaults:", {
        party,
        partyType,
        doctype,
        company,
        authMode,
      });

      try {
        // Call API based on auth mode
        let response: {
          success: boolean;
          data?: PartyDefaultsResult;
          error?: string;
          error_type?: string;
          warnings?: Array<{ severity: string; title: string; message: string }>;
        };

        const params = {
          party,
          party_type: partyType,
          doctype,
          ...(company && { company }),
        };

        if (authMode === "control_plane") {
          // Use tenantServer for control_plane mode (JWT auth)
          response = await tenantServer.call<typeof response>(
            "zaviago_backend.api.doc_calculation.get_party_defaults",
            params
          );
        } else {
          // Use frappeClient for direct mode (session auth)
          response = await frappeClient!.call<typeof response>(
            "zaviago_backend.api.doc_calculation.get_party_defaults",
            params
          );
        }

        console.log("[usePartyDefaults] Server response:", response);

        if (response.success && response.data) {
          console.log("[usePartyDefaults] Defaults fetched:", {
            currency: response.data.currency,
            selling_price_list: response.data.selling_price_list,
            plc_conversion_rate: response.data.plc_conversion_rate,
            taxes_and_charges: response.data.taxes_and_charges,
          });

          setResult(response.data);
          setError(null);
          onSuccess?.(response.data);
          return response.data;
        } else {
          const errorMsg = response.error || "Failed to fetch party defaults";
          console.error("[usePartyDefaults] Error:", errorMsg);
          setError(errorMsg);
          onError?.(errorMsg);
          return null;
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Failed to fetch party defaults";
        console.error("[usePartyDefaults] Exception:", errorMsg);
        setError(errorMsg);
        onError?.(errorMsg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [frappeClient, authMode, cpToken, partyType, doctype, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    fetchDefaults,
    result,
    isLoading,
    error,
    reset,
  };
}
